import React, { useState } from 'react';
import {
    Button,
    Row,
    Col,
    Badge,
    Card,
    CardBody
} from 'react-bootstrap';
import {
    FaTimes,
    FaMapMarkerAlt,
    FaClock,
    FaPhone,
    FaEnvelope,
    FaUser,
    FaCalendar,
    FaIdCard,
    FaBriefcase,
    FaFileAlt,
    FaCheckCircle,
    FaLanguage,
    FaSearchPlus,
    FaBuilding,
    FaGlobe,
    FaHome
} from 'react-icons/fa';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

// Fallback générique si aucune photo n'est disponible
const DEFAULT_AVATAR = 'https://via.placeholder.com/300?text=Photo+non+disponible';

const ProfessionalCandidateDetailModal = ({ professional, user, show, onClose }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState([]);

    if (!user || !show) return null;

    // ── Résout la meilleure URL de photo disponible ──────────────────────────────
    const resolveProfilePhoto = (userData) => {
        // Priorité à profilePhotoUrlPresigned (URL complète avec signature)
        if (userData.profilePhotoUrlPresigned) {
            return userData.profilePhotoUrlPresigned;
        }

        // Vérifier profilePhotoUrl
        if (userData.profilePhotoUrl) {
            if (userData.profilePhotoUrl.startsWith('http')) {
                return userData.profilePhotoUrl;
            }
            return `${process.env.REACT_APP_MINIO_URL || ''}/${userData.profilePhotoUrl}`;
        }

        // Fallback sur profilePictureUrl
        if (userData.profilePictureUrl) {
            if (userData.profilePictureUrl.startsWith('http')) {
                return userData.profilePictureUrl;
            }
            return `${process.env.REACT_APP_MINIO_URL || ''}/${userData.profilePictureUrl}`;
        }

        return DEFAULT_AVATAR;
    };

    // Ouvre la photo de profil en plein écran avec zoom
    const handleOpenPhotoPreview = (url) => {
        if (url && url !== DEFAULT_AVATAR) {
            setLightboxSlides([{ src: url }]);
            setLightboxOpen(true);
        }
    };

    const getServiceLabel = (service) => {
        if (!service) return 'Service non spécifié';

        const serviceMap = {
            'services_de_mnage': 'Services de Ménage',
            'plomberie': 'Plomberie',
            'electricite': 'Électricité',
            'peinture': 'Peinture',
            'menuiserie': 'Menuiserie',
            'maçonnerie': 'Maçonnerie',
            'SERVICE SUR MESURE': 'Service sur mesure'
        };

        return serviceMap[service] || service;
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'Non spécifié';
        try {
            const [datePart] = dateString.split(' ');
            const [day, month, year] = datePart.split('-');
            return `${day}/${month}/${year}`;
        } catch {
            return dateString;
        }
    };

    const DisplayField = ({ label, value, icon, emptyText = "Non spécifié" }) => (
        <div className="display-field mb-3">
            <div className="field-label">
                {icon} {label}
            </div>
            <div className="field-value">
                {value || emptyText}
            </div>
        </div>
    );

    // Résoudre l'URL de la photo
    const photoUrl = resolveProfilePhoto(user);

    // Récupérer les sous-services
    const subServices = user.subServices ? user.subServices.split(',').map(s => s.trim()) : [];

    // Récupérer les langues
    const languages = user.languages ? user.languages.split(/[,;]/).map(l => l.trim()).filter(Boolean) : [];

    return (
        <>
            {/* Overlay et modal principale */}
            <div
                className="professional-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="professional-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    {user.firstName} {user.lastName}
                                </h4>
                                <div className="d-flex gap-2 align-items-center flex-wrap mt-1">
                                    <small className="text-muted">{getServiceLabel(user.selectedService)}</small>
                                    <Badge bg="success" className="status-badge">
                                        <FaCheckCircle className="me-1" /> Validé
                                    </Badge>
                                </div>
                            </div>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>
                    </div>

                    {/* Contenu principal - Un seul bloc */}
                    <div className="p-4">
                        <div className="bg-light rounded-3 p-4">
                            {/* Section Photo et informations personnelles */}
                            <Row className="g-4 align-items-start">
                                {/* Photo de profil */}
                                <Col xs={12} md={3}>
                                    <div className="text-center">
                                        <div
                                            className="photo-container"
                                            style={{
                                                position: 'relative',
                                                width: '150px',
                                                height: '150px',
                                                margin: '0 auto',
                                                cursor: 'zoom-in',
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                border: '4px solid #e9ecef',
                                                backgroundColor: '#f8f9fa',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            onClick={() => handleOpenPhotoPreview(photoUrl)}
                                        >
                                            <img
                                                src={photoUrl}
                                                alt={`${user.firstName} ${user.lastName}`}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    objectPosition: 'center',
                                                    backgroundColor: '#f8f9fa',
                                                }}
                                                onError={(e) => {
                                                    e.target.src = DEFAULT_AVATAR;
                                                }}
                                            />
                                            <div
                                                className="photo-zoom-hint"
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    color: 'white',
                                                    fontSize: '24px',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s ease',
                                                    textShadow: '0 1px 6px rgba(0,0,0,0.5)',
                                                    pointerEvents: 'none',
                                                    backgroundColor: 'rgba(0,0,0,0.4)',
                                                    borderRadius: '50%',
                                                    padding: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <FaSearchPlus />
                                            </div>
                                        </div>
                                    </div>
                                </Col>

                                {/* Informations personnelles */}
                                <Col xs={12} md={9}>
                                    <h5 className="fw-bold mb-3 text-success">👤 Informations Personnelles</h5>
                                    <Row className="g-3">
                                        <Col xs={12} sm={6}>
                                            <DisplayField
                                                label="Prénom"
                                                value={user.firstName}
                                                icon={<FaUser />}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <DisplayField
                                                label="Nom"
                                                value={user.lastName}
                                                icon={<FaUser />}
                                            />
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <DisplayField
                                                label="Genre"
                                                value={user.gender === 'MALE' ? 'Homme' : user.gender === 'FEMALE' ? 'Femme' : user.gender}
                                                icon={<FaUser />}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <DisplayField
                                                label="Date de naissance"
                                                value={formatDateForDisplay(user.dateOfBirth)}
                                                icon={<FaCalendar />}
                                            />
                                        </Col>
                                        <Col xs={12}>
                                            <DisplayField
                                                label="Référence"
                                                value={user.useRef}
                                                icon={<FaIdCard />}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            <hr className="my-4" />

                            {/* Section Localisation */}
                            <Row className="mb-4">
                                <Col xs={12}>
                                    <h5 className="fw-bold mb-3 text-success">📍 Localisation</h5>
                                    <Row className="g-3">
                                        <Col xs={12} md={4}>
                                            <DisplayField
                                                label="Pays"
                                                value={user.country}
                                                icon={<FaGlobe />}
                                            />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <DisplayField
                                                label="Ville"
                                                value={user.city}
                                                icon={<FaBuilding />}
                                            />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <DisplayField
                                                label="Quartier"
                                                value={user.neighborhood}
                                                icon={<FaHome />}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            <hr className="my-4" />

                            {/* Section Professionnelle */}
                            <Row className="mb-4">
                                <Col xs={12}>
                                    <h5 className="fw-bold mb-3 text-success">💼 Informations Professionnelles</h5>
                                    <Row className="g-3">
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Service principal"
                                                value={getServiceLabel(user.selectedService)}
                                                icon={<FaBriefcase />}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Années d'expérience"
                                                value={user.yearsOfExperience ? `${user.yearsOfExperience} ans` : "Non spécifié"}
                                                icon={<FaClock />}
                                            />
                                        </Col>
                                        {subServices.length > 0 && (
                                            <Col xs={12}>
                                                <div className="display-field mb-3">
                                                    <div className="field-label">
                                                        <FaFileAlt className="me-2" />
                                                        Compétences
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                                        {subServices.map((subService, index) => (
                                                            <Badge key={index} bg="secondary" className="px-3 py-2">
                                                                {subService}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Col>
                                        )}
                                        {languages.length > 0 && (
                                            <Col xs={12}>
                                                <div className="display-field mb-3">
                                                    <div className="field-label">
                                                        <FaLanguage className="me-2" />
                                                        Langues parlées
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                                        {languages.map((language, index) => (
                                                            <Badge key={index} bg="primary" className="px-3 py-2">
                                                                {language}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Col>
                                        )}
                                        <Col xs={12}>
                                            <DisplayField
                                                label="À propos"
                                                value={user.about || user.additionalInfos || 'Aucune information supplémentaire'}
                                                icon={<FaFileAlt />}
                                            />
                                        </Col>

                                        <Col xs={12} md={6}>
                                            <DisplayField
                                                label="Statut"
                                                value={user.availabilityStatus ? 'Disponible' : 'Indisponible'}
                                                icon={<FaCheckCircle />}
                                            />
                                        </Col>

                                        {user.status && (
                                            <Col xs={12} md={6}>
                                                <DisplayField
                                                    label="Statut de validation"
                                                    value={user.status === 'ACCEPTED' ? 'Validé' : user.status === 'REJECTED' ? 'Rejeté' : 'En attente'}
                                                    icon={<FaCheckCircle />}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </Col>
                            </Row>


                        </div>
                    </div>

                    {/* Footer avec actions */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px', marginTop: 'auto'
                    }}>
                        <div className="d-flex justify-content-end w-100">
                            <Button variant="secondary" onClick={onClose} className="rounded-pill d-flex align-items-center gap-2">
                                <FaTimes /> Fermer
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lightbox pour la photo de profil avec zoom */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 3 }}
                controller={{ closeOnBackdropClick: true }}
            />

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                .professional-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }
                
                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }
                
                ::-webkit-scrollbar {
                    width: 6px;
                }
                
                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }
                
                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }
                
                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
                
                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.25rem;
                }
                
                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }
                
                .photo-container:hover .photo-zoom-hint {
                    opacity: 1 !important;
                }
            `}</style>
        </>
    );
};

export default ProfessionalCandidateDetailModal;